import { Checkbox, Select } from '@innovamat/glimmer-components';
import { validators } from '@innovamat/radiance-utils';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Input, InputPhone } from '@innovamat/glimmer-components';
import { isValidPhoneNumber } from 'react-phone-number-input';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type {
  InvitationType,
  UserInvitation,
} from '../../hooks/use-complete-invitation/invitation';
import { isValidUserInvitationForm } from './model/invitation/user-invitation';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Container = styled.div`
  display: flex;
  gap: 24px;
  width: 100%;
  > div {
    width: 100%;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  span {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.dark['03']};
    a {
      color: ${({ theme }) => theme.colors.dark['01']};
      text-decoration: underline;
    }
  }
`;

const StyledInputPhone = styled(InputPhone)<{ isFocus: boolean }>`
  .react-tel-input {
    height: 40px;
    > input {
      padding: 0px 8px;
      border: 1px solid rgb(148, 148, 148) !important;
      border-radius: 4px !important;
      height: 40px !important;
      &:focus {
        border: 2px solid rgb(51, 51, 51) !important;
      }
    }
    .flag-dropdown {
      border: ${({ theme }) =>
        `1px solid ${theme.colors.dark['03']} !important`};
      border-radius: 4px 0 0 4px !important;
      ${({ isFocus, theme }) =>
        isFocus &&
        css`
          border-top: ${`2px solid ${theme.colors.dark['01']} !important`};
          border-bottom: ${`2px solid ${theme.colors.dark['01']} !important`};
          border-left: ${`2px solid ${theme.colors.dark['01']} !important`};
        `}
    }
  }
`;

const StyledInput = styled.input`
  width: 0;
  height: 0;
  border: 0px;
  position: absolute;
`;

export type RegisterUserFormProps = {
  data: UserInvitation;
  userType: {
    type: InvitationType;
    isParent: boolean;
    isStudent: boolean;
    isTeacher: boolean;
  };
  languages: string[];
  region: string;
  email?: string;
  isPending: boolean;
  onSubmit: (userInvitation: UserInvitation) => void;
};

function CompleteInvitationForm({
  data,
  userType,
  languages,
  region,
  isPending,
  onSubmit,
}: RegisterUserFormProps): JSX.Element {
  const { t } = useTranslation();
  const { isParent, isStudent, isTeacher } = userType;
  const [userInvitation, setUserInvitation] = useState<UserInvitation>(data);
  const [isInputPhoneFocus, setIsInputPhoneFocus] = useState(false);
  const [inputPhoneDialCode, setInputPhoneDialCode] = useState();
  const [acceptTerms, setAcceptTerms] = useState(false);

  const { firstName, lastName, phoneNumber, password, language } =
    userInvitation;

  const handleChangePhone = (value: string, country: any) => {
    setUserInvitation({
      ...userInvitation,
      phoneNumber: `+${value}`,
    });

    if (country.dialCode !== inputPhoneDialCode) {
      setInputPhoneDialCode(country.dialCode);
    }
  };

  const handleChangeInput = (value: any, key: keyof UserInvitation) => {
    setUserInvitation({
      ...userInvitation,
      [key]: value,
    });
  };

  const handleCompleteUserInvitation = (e: React.FormEvent) => {
    e.preventDefault();
    const body = { ...userInvitation };
    if (
      (!isParent && !body.phoneNumber) ||
      !isValidPhoneNumber(body.phoneNumber || '')
    ) {
      body.phoneNumber = null;
    }
    onSubmit(body);
  };

  const langOptions = languages.map((lang) => ({
    value: lang,
    label: t(`language.${lang}`),
    key: lang,
  }));

  return (
    <Form onSubmit={handleCompleteUserInvitation}>
      <Container>
        <Input
          labelText={t(
            'teacher.onboarding.createProfileFilled.field.firstName'
          )}
          required
          onChange={(e) => handleChangeInput(e.target.value, 'firstName')}
          value={firstName}
          placeholder={t(
            'teacher.onboarding.createProfileFilled.placeholder.firstName'
          )}
          type="text"
          name="name"
          dataTestId="complete-invitation-name"
        />
        <Input
          labelText={t('teacher.onboarding.createProfileFilled.field.lastName')}
          required
          onChange={(e) => handleChangeInput(e.target.value, 'lastName')}
          value={lastName}
          placeholder={t(
            'teacher.onboarding.createProfileFilled.placeholder.lastName'
          )}
          type="text"
          name="surname"
          dataTestId="complete-invitation-surname"
        />
      </Container>

      {isTeacher && (
        <Input
          labelText={t('teacher.onboarding.createProfileFilled.field.email')}
          status="disabled"
          value={userInvitation.email}
          type="email"
          name="email"
          dataTestId="complete-invitation-email"
        />
      )}
      <div>
        {/* Needed so that Google's password manager detects there's an email input field and doesn't pick
          the surname field as the email field and autocompletes it there */}
        <StyledInput type="email" name="email" />
        <Input
          labelText={t('teacher.onboarding.createProfileFilled.field.password')}
          descriptiveText={t(
            'teacher.onboarding.createProfileFilled.password.tagline'
          )}
          required
          name="password"
          type="password"
          value={password}
          status={
            password && !validators.passwordIsValid(password)
              ? 'error'
              : undefined
          }
          placeholder={t(
            'teacher.onboarding.createProfileFilled.placeholder.password'
          )}
          onChange={(e) => handleChangeInput(e.target.value, 'password')}
          dataTestId="complete-invitation-password"
        />
      </div>
      {isTeacher && (
        <div
          onMouseEnter={() => setIsInputPhoneFocus(true)}
          onMouseLeave={() => setIsInputPhoneFocus(false)}
        >
          <StyledInputPhone
            isFocus={isInputPhoneFocus}
            label={t('teacher.onboarding.createProfileFilled.field.phone')}
            phone={phoneNumber || ''}
            onChange={handleChangePhone}
            error={
              !validators.isPhoneValid(phoneNumber, inputPhoneDialCode || '')
            }
            description={
              !validators.isPhoneValid(phoneNumber, inputPhoneDialCode || '')
                ? t('teacher.onboarding.createProfileFilled.phoneNumber.error')
                : t(
                    'teacher.onboarding.createProfileFilled.phoneNumber.tagline'
                  )
            }
            optionalText={t('input.optional')}
            country={region.toLowerCase()}
            dataTestId="complete-invitation-phone"
          />
        </div>
      )}
      {!isStudent && (
        <Select
          value={language}
          options={langOptions}
          labelText={t(
            'teacher.onboarding.createProfileFilled.field.platformLanguage'
          )}
          onChange={(opt) => handleChangeInput(opt?.value || '', 'language')}
          dataTestId="complete-invitation-language"
        />
      )}
      <CheckboxContainer>
        <Checkbox
          checked={acceptTerms || false}
          onChange={(e) => setAcceptTerms(e.target.checked)}
          data-testid="complete-invitation-terms"
        />
        <span
          dangerouslySetInnerHTML={{
            __html: t('register.personal_data.terms_and_privacy'),
          }}
        />
      </CheckboxContainer>
      {isParent && (
        <CheckboxContainer>
          <Checkbox
            checked={userInvitation.parentAcceptCommunication}
            onChange={() =>
              setUserInvitation({
                ...userInvitation,
                parentAcceptCommunication:
                  !userInvitation.parentAcceptCommunication,
              })
            }
            data-testid="complete-invitation-communications"
          />
          <span>{t('parent_register_form.accept_communications')}</span>
        </CheckboxContainer>
      )}

      <Button
        fill
        type="submit"
        disabled={
          !isValidUserInvitationForm({
            ...userInvitation,
            phoneDialCode: inputPhoneDialCode || '',
            acceptTerms,
          })
        }
        onClick={handleCompleteUserInvitation}
        loading={isPending}
        data-testid="complete-invitation-submit"
      >
        {t('teacher.onboarding.createProfileFilled.button.send')}
      </Button>
    </Form>
  );
}

export { CompleteInvitationForm };
